import React, { useCallback, useEffect, useState, useMemo } from 'react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { UserAvatar } from '@/components/UserAvatar';
import ConsensusRing from '@/components/itinerary/ConsensusRing';
import ActivityCard from '@/components/itinerary/ActivityCard';
import TransitConnector from '@/components/itinerary/TransitConnector';
import ActivityEditorSheet from '@/components/itinerary/ActivityEditorSheet';
import { api } from '@/lib/api';
import { ITINERARY } from '@/constants/testIds';
import { personalityMeta } from '@/lib/personalities';
import { Sparkles, RefreshCw, ChevronDown, Download, MessageCircle, Wand2, Sun, Trophy, Lock, Plus } from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { toast } from 'sonner';
import { Reorder, useDragControls } from 'framer-motion';

// Background-job polling for the async itinerary generate flow (Stage 17).
const POLL_INTERVAL_MS = 3000;
const POLL_MAX_ATTEMPTS = 200; // 200 * 3s = 10 minutes

// Stable client-side uid so framer-motion Reorder.Item can track activities across reorders.
let _activityUidCounter = 0;
const _activityUidWeakMap = new WeakMap();
function ensureActivityUid(activity) {
    if (!activity || typeof activity !== 'object') return `act-${++_activityUidCounter}`;
    if (activity.__uid) return activity.__uid;
    let uid = _activityUidWeakMap.get(activity);
    if (!uid) {
        uid = `act-${++_activityUidCounter}`;
        _activityUidWeakMap.set(activity, uid);
        try { activity.__uid = uid; } catch { /* frozen object — fall back to map */ }
    }
    return uid;
}

function decorateItinerary(itinerary) {
    if (!itinerary?.days) return itinerary;
    return {
        ...itinerary,
        days: itinerary.days.map(d => ({
            ...d,
            activities: (d.activities || []).map(a => {
                const next = { ...a };
                next.__uid = ensureActivityUid(a);
                return next;
            }),
        })),
    };
}

function formatDayDate(iso) {
    try { return format(parseISO(iso), 'EEEE, d MMM'); } catch { return iso; }
}

function PersonalityBalanceBar({ counts, suggestion }) {
    const entries = Object.entries(counts || {});
    const total = entries.reduce((s, [, n]) => s + Number(n || 0), 0);
    if (total === 0) return null;
    return (
        <div data-testid={ITINERARY.personalityBalance}>
            <div className="text-xs uppercase tracking-[0.18em] text-[var(--ns-text3)] mb-2">Personality balance</div>
            <div className="flex h-3 rounded-full overflow-hidden border border-[var(--ns-border)]">
                {entries.map(([p, n]) => {
                    const w = (Number(n) / total) * 100;
                    const meta = personalityMeta(p);
                    return (
                        <div
                            key={p}
                            style={{ width: `${w}%`, background: meta.color }}
                            title={`${p}: ${n}`}
                        />
                    );
                })}
            </div>
            <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2 text-xs text-[var(--ns-text2)]">
                {entries.map(([p, n]) => {
                    const meta = personalityMeta(p);
                    return (
                        <span key={p} className="inline-flex items-center gap-1">
                            <span className="w-2 h-2 rounded-full" style={{ background: meta.color }} />
                            {p.replace('The ', '')} · {n}
                        </span>
                    );
                })}
            </div>
            {suggestion && (
                <p className="text-xs text-[var(--ns-text2)] mt-3 italic">{suggestion}</p>
            )}
        </div>
    );
}

function PerPersonRow({ items }) {
    if (!items?.length) return null;
    const least = items.reduce((min, p) => (p.satisfaction < (min?.satisfaction ?? 101) ? p : min), null);
    return (
        <div>
            <div className="text-xs uppercase tracking-[0.18em] text-[var(--ns-text3)] mb-3">Per-person satisfaction</div>
            <div className="space-y-2">
                {items.map(p => (
                    <div
                        key={p.user_id}
                        data-testid={ITINERARY.perPersonRow}
                        className="flex items-center gap-3"
                    >
                        <UserAvatar name={p.name} size={28} />
                        <div className="flex-1 min-w-0">
                            <div className="flex items-baseline justify-between gap-2">
                                <span className="text-sm font-medium text-[var(--ns-text)] truncate">{p.name}</span>
                                <span className="text-sm font-semibold tabular-nums text-[var(--ns-text)]">{p.satisfaction}%</span>
                            </div>
                            <div className="w-full h-1.5 rounded-full bg-[var(--ns-soft)] overflow-hidden mt-1">
                                <div
                                    className="h-full transition-all"
                                    style={{
                                        width: `${p.satisfaction}%`,
                                        background: p.satisfaction >= 75 ? 'var(--ns-success)' : p.satisfaction >= 50 ? '#C77B3A' : 'var(--ns-clay)',
                                    }}
                                />
                            </div>
                            {p.explanation && <div className="text-xs text-[var(--ns-text3)] mt-1.5">{p.explanation}</div>}
                        </div>
                    </div>
                ))}
            </div>
            {least && least.satisfaction < 75 && (
                <div
                    data-testid={ITINERARY.leastSatisfiedCallout}
                    className="mt-4 rounded-2xl border border-[var(--ns-border)] p-4 flex items-start gap-3"
                    style={{ background: 'rgba(224,122,95,0.08)' }}
                >
                    <MessageCircle size={16} className="text-[var(--ns-clay)] mt-0.5 flex-shrink-0" />
                    <p className="text-sm text-[var(--ns-text)] leading-relaxed">
                        <strong>{least.name}</strong>&apos;s satisfaction is lowest at {least.satisfaction}%. {least.explanation}
                    </p>
                </div>
            )}
        </div>
    );
}

function DraggableActivity({ activity, dayNumber, availableDays, tripDestination, onUpdateField, onMoveToDay, showTransit, onEditActivity, onSaveNote }) {
    const controls = useDragControls();
    const [dragging, setDragging] = useState(false);
    return (
        <Reorder.Item
            value={activity}
            dragListener={false}
            dragControls={controls}
            onDragStart={() => setDragging(true)}
            onDragEnd={() => setDragging(false)}
            whileDrag={{ zIndex: 50 }}
            className="list-none"
        >
            {showTransit && (
                <TransitConnector
                    minutes={activity.transit_minutes}
                    mode={activity.transit_mode}
                />
            )}
            <ActivityCard
                activity={activity}
                tripDestination={tripDestination}
                onUpdateField={onUpdateField}
                onMoveToDay={onMoveToDay}
                onEditActivity={onEditActivity}
                onSaveNote={onSaveNote}
                availableDays={availableDays}
                currentDayNumber={dayNumber}
                dragControls={controls}
                isDragging={dragging}
            />
        </Reorder.Item>
    );
}

function AddBetweenButton({ dayNumber, position, onClick, testid }) {
    return (
        <div className="flex justify-center -my-1">
            <button
                type="button"
                onClick={() => onClick(dayNumber, position)}
                data-testid={testid}
                className="group h-6 px-3 rounded-full border border-dashed border-[var(--ns-border)] bg-white text-[10px] uppercase tracking-widest text-[var(--ns-text3)] hover:text-[var(--ns-clay)] hover:border-[var(--ns-clay)] transition opacity-40 hover:opacity-100 inline-flex items-center gap-1"
                title="Insert activity here"
                aria-label="Insert activity"
            >
                <Plus size={11} /> Add here
            </button>
        </div>
    );
}

function DayActivities({ day, availableDays, tripDestination, onCommitReorder, onUpdateField, onMoveToDay, onEditActivity, onSaveNote, onAddActivity }) {
    const activities = day.activities || [];
    return (
        <div>
            <Reorder.Group
                axis="y"
                values={activities}
                onReorder={(next) => onCommitReorder(day.day_number, next)}
                className="space-y-3 list-none p-0 m-0"
                data-testid={`itinerary-day-activities-${day.day_number}`}
            >
                {activities.map((a, idx) => (
                    <React.Fragment key={a.__uid || `${day.day_number}-${a.location_name}-${idx}`}>
                        {idx > 0 && (
                            <AddBetweenButton
                                dayNumber={day.day_number}
                                position={idx}
                                onClick={onAddActivity}
                                testid={`${ITINERARY.addActivityBetween}-${day.day_number}-${idx}`}
                            />
                        )}
                        <DraggableActivity
                            activity={a}
                            dayNumber={day.day_number}
                            availableDays={availableDays}
                            tripDestination={tripDestination}
                            onUpdateField={(field, value) => onUpdateField(day.day_number, activities.indexOf(a), field, value)}
                            onMoveToDay={(toDay) => onMoveToDay(day.day_number, activities.indexOf(a), toDay)}
                            onEditActivity={() => onEditActivity(day.day_number, activities.indexOf(a))}
                            onSaveNote={(note) => onSaveNote(day.day_number, activities.indexOf(a), note)}
                            showTransit={idx > 0}
                        />
                    </React.Fragment>
                ))}
            </Reorder.Group>
            <div className="mt-4 flex justify-center">
                <button
                    type="button"
                    onClick={() => onAddActivity(day.day_number, activities.length)}
                    data-testid={`${ITINERARY.addActivityButton}-${day.day_number}`}
                    className="h-10 px-5 rounded-full border border-dashed border-[var(--ns-border)] bg-white text-sm text-[var(--ns-text2)] hover:text-[var(--ns-clay)] hover:border-[var(--ns-clay)] transition inline-flex items-center gap-2"
                >
                    <Plus size={14} /> Add activity to Day {day.day_number}
                </button>
            </div>
        </div>
    );
}

export default function ItineraryTab({ trip, refreshKey, onUpgrade }) {
    const tripId = trip.trip_id;
    const [loading, setLoading] = useState(true);
    const [itinerary, setItinerary] = useState(null);
    const [generating, setGenerating] = useState(false);
    const [emphasis, setEmphasis] = useState('none');
    const [specialRequests, setSpecialRequests] = useState('');
    const [generationCount, setGenerationCount] = useState(0);
    const [conflictsOpen, setConflictsOpen] = useState(true);
    const [isPremium, setIsPremium] = useState(!!trip.is_premium);
    const [freeLimit, setFreeLimit] = useState(2);
    const [activeDay, setActiveDay] = useState('all');
    // Editor sheet state
    const [editorOpen, setEditorOpen] = useState(false);
    const [editorMode, setEditorMode] = useState('add'); // 'add' | 'edit'
    const [editorDay, setEditorDay] = useState(1);
    const [editorPosition, setEditorPosition] = useState(0);
    const [editorActivity, setEditorActivity] = useState(null);
    const [editorActivityIndex, setEditorActivityIndex] = useState(null);

    const load = useCallback(async () => {
        try {
            const { data } = await api.get(`/trips/${tripId}/itinerary`);
            setItinerary(decorateItinerary(data.itinerary));
            setGenerationCount(data.generation_count || 0);
            setIsPremium(!!data.is_premium);
            setFreeLimit(data.free_generation_limit || 2);
        } finally { setLoading(false); }
    }, [tripId]);

    useEffect(() => { load(); }, [load, refreshKey]);

    const personalityOptions = useMemo(() => {
        const set = new Set();
        (itinerary?.personality_balance?.counts ? Object.keys(itinerary.personality_balance.counts) : [])
            .forEach(p => set.add(p));
        // Always include the canonical 8
        ['The Explorer', 'The Foodie', 'The Photographer', 'The Relaxed Wanderer', 'The Efficiency Maximiser', 'The Night Owl', 'The Shopaholic', 'The Adventurer']
            .forEach(p => set.add(p));
        return Array.from(set);
    }, [itinerary]);

    const generate = async (opts = {}) => {
        setGenerating(true);
        try {
            const body = {};
            if (opts.emphasis_personality) body.emphasis_personality = opts.emphasis_personality;
            const trimmed = (specialRequests || '').trim();
            if (trimmed) body.special_requests = trimmed;
            const { data: startData } = await api.post(`/trips/${tripId}/itinerary/generate`, body);
            const jobId = startData?.job_id;
            if (!jobId) {
                throw new Error('No job_id returned from server');
            }

            // Poll every 3s until complete/error or the timeout.
            const finalResult = await new Promise((resolve, reject) => {
                let attempts = 0;
                const tick = async () => {
                    attempts += 1;
                    try {
                        const { data: jobData } = await api.get(`/trips/${tripId}/itinerary/job/${jobId}`);
                        if (jobData?.status === 'complete') {
                            resolve(jobData.result || null);
                            return;
                        }
                        if (jobData?.status === 'error') {
                            reject(new Error(jobData?.error_message || 'Generation failed'));
                            return;
                        }
                    } catch (pollErr) {
                        // Fail fast on 401 (session expired), 403 (no longer a participant), 404 (job expired).
                        const s = pollErr?.response?.status;
                        if (s === 401 || s === 403 || s === 404) {
                            reject(pollErr);
                            return;
                        }
                        // Any other transient network error: swallow and retry
                    }
                    if (attempts >= POLL_MAX_ATTEMPTS) {
                        reject(new Error('Generation is taking too long. Please try again.'));
                        return;
                    }
                    setTimeout(tick, POLL_INTERVAL_MS);
                };
                setTimeout(tick, POLL_INTERVAL_MS);
            });

            if (!finalResult) throw new Error('Generation returned no result');
            setItinerary(decorateItinerary(finalResult.itinerary));
            setGenerationCount(finalResult.generation_count || 0);
            setIsPremium(!!finalResult.is_premium);
            setSpecialRequests('');
            toast.success(opts.emphasis_personality ? `Regenerated with focus on ${opts.emphasis_personality}` : 'Itinerary ready!');
        } catch (err) {
            if (err?.response?.status === 402) {
                toast.error(err.response.data?.detail || 'Premium required');
                onUpgrade?.();
            } else {
                toast.error(err?.message || err?.response?.data?.detail || 'Could not generate itinerary right now');
            }
        } finally {
            setGenerating(false);
        }
    };

    // Merge backend-returned `days` (post-cascade) into local itinerary while preserving uids.
    const absorbDays = (newDays) => {
        setItinerary((prev) => {
            if (!prev) return prev;
            const prevByDay = new Map(prev.days.map(d => [d.day_number, d.activities || []]));
            const merged = (newDays || []).map(d => {
                const prevActs = prevByDay.get(d.day_number) || [];
                const acts = (d.activities || []).map((a, i) => {
                    // Match by reference where possible (server returns the same shape; we re-key by index
                    // within the new array. Backend cascade keeps order so this is stable.)
                    const carry = prevActs.find(pa => pa.location_name === a.location_name && pa.description === a.description);
                    const next = { ...a };
                    next.__uid = (carry && carry.__uid) || prevActs[i]?.__uid || ensureActivityUid(a);
                    return next;
                });
                return { ...d, activities: acts };
            });
            return { ...prev, days: merged };
        });
    };

    // Called by framer-motion Reorder.Group onReorder with the new activity array for one day.
    const commitReorder = async (dayNumber, newActivities) => {
        const day = itinerary?.days.find(d => d.day_number === dayNumber);
        if (!day) return;
        // Build the permutation of original indices from the new ordering.
        const order = newActivities.map(na => {
            const found = day.activities.findIndex(a => (a.__uid && na.__uid && a.__uid === na.__uid) || a === na);
            return found >= 0 ? found : 0;
        });
        // Reject no-op reorders (same order).
        if (order.every((v, i) => v === i)) return;
        // Optimistic update (already reflected by Reorder.Group locally, but ensure parent state matches)
        setItinerary(prev => prev ? ({
            ...prev,
            days: prev.days.map(d => d.day_number === dayNumber ? { ...d, activities: newActivities } : d),
        }) : prev);
        try {
            const { data } = await api.patch(`/trips/${tripId}/itinerary/reorder`, { day_number: dayNumber, order });
            if (data?.days) absorbDays(data.days);
        } catch (err) {
            toast.error(err?.response?.data?.detail || 'Could not reorder');
            load();
        }
    };

    const updateActivityField = async (dayNumber, idx, field, value) => {
        const prevState = itinerary;
        // Optimistic update of just the edited field
        setItinerary(prev => prev ? ({
            ...prev,
            days: prev.days.map(d => {
                if (d.day_number !== dayNumber) return d;
                const acts = d.activities.map((a, i) => i === idx ? { ...a, [field]: value } : a);
                return { ...d, activities: acts };
            }),
        }) : prev);
        try {
            const body = { day_number: dayNumber, activity_index: idx };
            body[field] = value;
            const { data } = await api.patch(`/trips/${tripId}/itinerary/activity`, body);
            // Backend returns cascaded days — sync them so subsequent times update visually
            if (data?.days) absorbDays(data.days);
        } catch (err) {
            setItinerary(prevState);
            toast.error(err?.response?.data?.detail || `Could not update ${field === 'time_slot' ? 'time' : 'duration'}`);
        }
    };

    const moveActivityToDay = async (fromDay, idx, toDay) => {
        if (fromDay === toDay) return;
        const prevState = itinerary;
        const src = itinerary.days.find(d => d.day_number === fromDay);
        if (!src) return;
        const activity = src.activities[idx];
        if (!activity) return;
        // Optimistic
        setItinerary(prev => prev ? ({
            ...prev,
            days: prev.days.map(d => {
                if (d.day_number === fromDay) {
                    return { ...d, activities: d.activities.filter((_, i) => i !== idx) };
                }
                if (d.day_number === toDay) {
                    return { ...d, activities: [...(d.activities || []), activity] };
                }
                return d;
            }),
        }) : prev);
        try {
            const { data } = await api.post(`/trips/${tripId}/itinerary/activity/move`, {
                from_day_number: fromDay,
                activity_index: idx,
                to_day_number: toDay,
            });
            if (data?.days) absorbDays(data.days);
            toast.success(`Moved to Day ${toDay}`);
        } catch (err) {
            setItinerary(prevState);
            toast.error(err?.response?.data?.detail || 'Could not move activity');
        }
    };

    // --- Manual add / edit / delete / note ---
    const openAddEditor = (dayNumber, position) => {
        setEditorMode('add');
        setEditorDay(dayNumber);
        setEditorPosition(position);
        setEditorActivity(null);
        setEditorActivityIndex(null);
        setEditorOpen(true);
    };

    const openEditEditor = (dayNumber, idx) => {
        const day = itinerary?.days.find(d => d.day_number === dayNumber);
        const act = day?.activities?.[idx];
        if (!act) return;
        setEditorMode('edit');
        setEditorDay(dayNumber);
        setEditorPosition(idx);
        setEditorActivity(act);
        setEditorActivityIndex(idx);
        setEditorOpen(true);
    };

    const submitActivityEditor = async (payload, mode) => {
        const itineraryId = itinerary?.itinerary_id;
        if (!itineraryId) throw new Error('Itinerary not ready');
        if (mode === 'add') {
            const { data } = await api.post(
                `/trips/${tripId}/itinerary/${itineraryId}/activities`,
                { ...payload, position: editorPosition },
            );
            if (data?.days) absorbDays(data.days);
            toast.success('Activity added');
        } else {
            const { data } = await api.patch(
                `/trips/${tripId}/itinerary/${itineraryId}/activities/${editorActivityIndex}`,
                payload,
            );
            if (data?.days) absorbDays(data.days);
            toast.success('Activity updated');
        }
    };

    const deleteEditingActivity = async () => {
        const itineraryId = itinerary?.itinerary_id;
        if (!itineraryId || editorActivityIndex === null) return;
        const { data } = await api.delete(
            `/trips/${tripId}/itinerary/${itineraryId}/activities/${editorActivityIndex}?day_number=${editorDay}`,
        );
        if (data?.days) absorbDays(data.days);
        toast.success('Activity deleted');
    };

    const saveActivityNote = async (dayNumber, idx, note) => {
        const itineraryId = itinerary?.itinerary_id;
        if (!itineraryId) return;
        // Optimistic
        const prevState = itinerary;
        setItinerary(prev => prev ? ({
            ...prev,
            days: prev.days.map(d => d.day_number !== dayNumber ? d : ({
                ...d,
                activities: d.activities.map((a, i) => i === idx ? { ...a, note } : a),
            })),
        }) : prev);
        try {
            const { data } = await api.patch(
                `/trips/${tripId}/itinerary/${itineraryId}/activities/${idx}`,
                { day_number: dayNumber, note },
            );
            if (data?.days) absorbDays(data.days);
            toast.success(note ? 'Note saved' : 'Note cleared');
        } catch (err) {
            setItinerary(prevState);
            toast.error(err?.response?.data?.detail || 'Could not save note');
            throw err;
        }
    };

    const exportPdf = () => {
        if (!isPremium) {
            onUpgrade?.();
            return;
        }
        // Use bearer token via query? Safer to fetch as blob with axios then download.
        api.get(`/trips/${tripId}/itinerary/export.pdf`, { responseType: 'blob' }).then(({ data }) => {
            const url = URL.createObjectURL(new Blob([data], { type: 'application/pdf' }));
            const a = document.createElement('a');
            a.href = url;
            a.download = `${(trip.name || 'itinerary').replace(/[^a-zA-Z0-9_-]/g, '_')}_itinerary.pdf`;
            document.body.appendChild(a); a.click(); a.remove();
            URL.revokeObjectURL(url);
        }).catch(err => {
            if (err?.response?.status === 402) {
                onUpgrade?.();
            } else {
                toast.error(err?.response?.data?.detail || 'Export failed');
            }
        });
    };

    if (loading) return <div className="ns-card p-12 text-center text-[var(--ns-text2)] mt-6">Loading itinerary…</div>;

    // Empty state — no itinerary yet
    if (!itinerary) {
        const submitted = trip.preferences_submitted || 0;
        const total = trip.preferences_total || 0;
        const ready = submitted >= 1 && (trip.ideas_count || 0) >= 1;
        return (
            <div data-testid={ITINERARY.emptyState} className="ns-card p-10 sm:p-14 text-center mt-6">
                <div className="mx-auto w-14 h-14 rounded-2xl flex items-center justify-center mb-4" style={{ background: 'var(--ns-soft)' }}>
                    <Sparkles className="text-[var(--ns-clay)]" />
                </div>
                <h3 className="font-display text-2xl sm:text-3xl font-semibold tracking-tight">Ready to plan?</h3>
                <p className="text-[var(--ns-text2)] mt-2 max-w-md mx-auto leading-relaxed">
                    NextStop will weave your ideas, votes, preferences, and personalities into a day-by-day plan that maximises group satisfaction.
                </p>
                <div className="text-xs text-[var(--ns-text3)] mt-4">
                    Preferences {submitted}/{total} · {trip.ideas_count || 0} ideas saved
                </div>
                <div className="mt-6 max-w-lg mx-auto text-left">
                    <label className="text-[10px] uppercase tracking-[0.18em] text-[var(--ns-text3)] block mb-1.5">
                        Special requests <span className="normal-case tracking-normal text-[var(--ns-text3)]">(optional)</span>
                    </label>
                    <Textarea
                        value={specialRequests}
                        onChange={(e) => setSpecialRequests(e.target.value)}
                        rows={3}
                        maxLength={1200}
                        placeholder="e.g. I would like to enjoy a nice Korean barbecue meal on the last day, avoid too much walking on the first few days…"
                        data-testid={ITINERARY.specialRequestsInput}
                        className="rounded-2xl border-[var(--ns-border)] bg-white text-sm resize-none"
                    />
                    <div className="flex justify-end text-[10px] text-[var(--ns-text3)] mt-1 tabular-nums">
                        {specialRequests.length}/1200
                    </div>
                </div>
                <Button
                    onClick={() => generate()}
                    disabled={generating || !ready}
                    data-testid={ITINERARY.generateButton}
                    className="mt-4 rounded-full h-12 px-7 text-white font-medium"
                    style={{ background: 'var(--ns-navy)' }}
                >
                    {generating ? 'Generating…' : <><Sparkles size={16} className="mr-2" /> Generate itinerary</>}
                </Button>
                {!ready && (
                    <p className="text-xs text-[var(--ns-text3)] mt-3">Add at least one idea and one set of preferences first.</p>
                )}
            </div>
        );
    }

    const limitReached = !isPremium && generationCount >= freeLimit;

    return (
        <div data-testid={ITINERARY.container} className="mt-6 space-y-8">
            {/* Score + Per-person + Personality balance */}
            <div className="ns-card p-6 sm:p-8">
                <div className="flex flex-col lg:flex-row lg:items-center gap-8">
                    <div className="flex flex-col items-center lg:items-start">
                        <ConsensusRing score={itinerary.consensus_score} />
                        <p data-testid={ITINERARY.consensusScore} className="font-display text-lg font-semibold mt-3 text-center lg:text-left leading-snug max-w-xs">
                            This itinerary satisfies <span style={{ color: 'var(--ns-clay)' }}>{itinerary.consensus_score}%</span> of group preferences.
                        </p>
                    </div>
                    <div className="flex-1 space-y-6 min-w-0">
                        {isPremium ? (
                            <>
                                <PerPersonRow items={itinerary.per_person_satisfaction} />
                                <PersonalityBalanceBar
                                    counts={itinerary.personality_balance?.counts}
                                    suggestion={itinerary.personality_balance?.suggestion}
                                />
                            </>
                        ) : (
                            <div
                                data-testid="itinerary-insights-locked"
                                className="rounded-2xl border border-dashed border-[var(--ns-border)] p-5 flex items-start gap-4"
                                style={{ background: 'var(--ns-soft)' }}
                            >
                                <span className="w-10 h-10 rounded-2xl flex items-center justify-center flex-shrink-0 bg-white border border-[var(--ns-border)]">
                                    <Lock size={16} className="text-[var(--ns-clay)]" />
                                </span>
                                <div className="flex-1">
                                    <div className="flex items-center gap-2 flex-wrap">
                                        <span className="font-display font-semibold text-[var(--ns-text)]">Per-person satisfaction & personality balance</span>
                                        <span className="text-[10px] uppercase tracking-widest font-semibold px-2 py-0.5 rounded-full" style={{ background: 'var(--ns-clay)', color: 'white' }}>Premium</span>
                                    </div>
                                    <p className="text-sm text-[var(--ns-text2)] mt-1">See how happy each traveler is and how well the plan balances every personality.</p>
                                    <Button
                                        onClick={onUpgrade}
                                        className="mt-3 rounded-full h-9 px-4 text-white text-xs"
                                        style={{ background: 'var(--ns-navy)' }}
                                    >
                                        <Sparkles size={12} className="mr-1.5" /> Unlock for $6.99
                                    </Button>
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                {/* Special requests for regeneration */}
                <div className="mt-6 pt-6 border-t border-[var(--ns-border)]">
                    <label className="text-[10px] uppercase tracking-[0.18em] text-[var(--ns-text3)] block mb-1.5">
                        Special requests for the next regeneration <span className="normal-case tracking-normal text-[var(--ns-text3)]">(optional)</span>
                    </label>
                    <Textarea
                        value={specialRequests}
                        onChange={(e) => setSpecialRequests(e.target.value)}
                        rows={2}
                        maxLength={1200}
                        placeholder="e.g. Swap the Day 2 museum for a food tour, more downtime in the mornings, add a rooftop bar on the last evening…"
                        data-testid={ITINERARY.specialRequestsInput}
                        className="rounded-2xl border-[var(--ns-border)] bg-white text-sm resize-none"
                    />
                    <div className="flex justify-between items-center text-[10px] text-[var(--ns-text3)] mt-1">
                        <span>Cleared automatically after each successful regeneration.</span>
                        <span className="tabular-nums">{specialRequests.length}/1200</span>
                    </div>
                </div>

                {/* Regenerate controls */}
                <div className="flex flex-col sm:flex-row sm:items-center gap-3 mt-4 pt-4 border-t border-[var(--ns-border)]">
                    <span data-testid={ITINERARY.generationCount} className="text-xs text-[var(--ns-text3)] inline-flex items-center gap-1.5">
                        <Trophy size={12} /> Generated {generationCount} time{generationCount === 1 ? '' : 's'}
                        {!isPremium && <span className="text-[var(--ns-clay)]">· {Math.max(0, freeLimit - generationCount)} free left</span>}
                    </span>
                    <div className="sm:ml-auto flex items-center gap-2 flex-wrap">
                        {isPremium && (
                            <Select value={emphasis} onValueChange={setEmphasis}>
                                <SelectTrigger data-testid={ITINERARY.emphasisSelect} className="h-10 rounded-full border-[var(--ns-border)] bg-white min-w-[200px]">
                                    <SelectValue placeholder="Emphasis…" />
                                </SelectTrigger>
                                <SelectContent className="rounded-2xl">
                                    <SelectItem value="none">No emphasis</SelectItem>
                                    {personalityOptions.map(p => (
                                        <SelectItem key={p} value={p}>Emphasise {p}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        )}
                        {!isPremium && (
                            <Button
                                variant="outline"
                                onClick={onUpgrade}
                                data-testid="emphasis-locked-button"
                                className="rounded-full h-10 px-4 border-dashed border-[var(--ns-border)] text-[var(--ns-text2)]"
                            >
                                <Lock size={14} className="mr-2" /> Emphasis (Premium)
                            </Button>
                        )}
                        {limitReached ? (
                            <Button
                                onClick={onUpgrade}
                                data-testid={ITINERARY.regenerateButton}
                                className="rounded-full h-10 px-4 text-white"
                                style={{ background: 'var(--ns-clay)' }}
                            >
                                <Sparkles size={14} className="mr-2" /> Upgrade to regenerate
                            </Button>
                        ) : (
                            <Button
                                variant="outline"
                                onClick={() => generate({ emphasis_personality: isPremium && emphasis !== 'none' ? emphasis : undefined })}
                                disabled={generating}
                                data-testid={ITINERARY.regenerateButton}
                                className="rounded-full h-10 px-4 border-[var(--ns-border)]"
                            >
                                <RefreshCw size={14} className={`mr-2 ${generating ? 'animate-spin' : ''}`} /> {isPremium && emphasis !== 'none' ? 'Regenerate with focus' : 'Regenerate'}
                            </Button>
                        )}
                        <Button
                            onClick={exportPdf}
                            data-testid={ITINERARY.exportPdfButton}
                            className="rounded-full h-10 px-4 text-white"
                            style={{ background: isPremium ? 'var(--ns-clay)' : 'var(--ns-text3)' }}
                            title={isPremium ? 'Export PDF' : 'Premium · upgrade to export'}
                        >
                            {isPremium ? <><Download size={14} className="mr-2" /> Export PDF</> : <><Lock size={14} className="mr-2" /> Export (Premium)</>}
                        </Button>
                    </div>
                </div>
            </div>

            {/* Conflict resolutions — PREMIUM */}
            {isPremium ? (
                itinerary.conflict_resolutions?.length > 0 && (
                    <Collapsible open={conflictsOpen} onOpenChange={setConflictsOpen}>
                        <CollapsibleTrigger
                            data-testid={ITINERARY.conflictResolutionsToggle}
                            className="w-full ns-card p-5 flex items-center justify-between text-left hover:bg-[var(--ns-soft)] transition"
                        >
                            <div className="flex items-center gap-3">
                                <span className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: 'rgba(29,45,68,0.08)' }}>
                                    <Wand2 size={16} className="text-[var(--ns-navy)]" />
                                </span>
                                <div>
                                    <div className="font-display font-semibold text-[var(--ns-text)]">How we resolved your conflicts</div>
                                    <div className="text-xs text-[var(--ns-text3)]">{itinerary.conflict_resolutions.length} compromise{itinerary.conflict_resolutions.length === 1 ? '' : 's'}</div>
                                </div>
                            </div>
                            <ChevronDown size={18} className={`text-[var(--ns-text3)] transition-transform ${conflictsOpen ? 'rotate-180' : ''}`} />
                        </CollapsibleTrigger>
                        <CollapsibleContent className="space-y-3 mt-3">
                            {itinerary.conflict_resolutions.map((c, idx) => (
                                <div
                                    key={idx}
                                    data-testid={ITINERARY.conflictResolutionItem}
                                    className="ns-card p-5 sm:p-6"
                                >
                                    <div className="font-display font-semibold text-[var(--ns-text)]">{c.title}</div>
                                    <p className="text-sm text-[var(--ns-text2)] mt-1.5 leading-relaxed">{c.resolution}</p>
                                </div>
                            ))}
                        </CollapsibleContent>
                    </Collapsible>
                )
            ) : (
                <div
                    data-testid="conflict-resolutions-locked"
                    className="ns-card p-6 sm:p-8 border-dashed flex items-start gap-4"
                >
                    <span className="w-10 h-10 rounded-2xl flex items-center justify-center flex-shrink-0" style={{ background: 'var(--ns-soft)' }}>
                        <Lock size={16} className="text-[var(--ns-clay)]" />
                    </span>
                    <div className="flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-display text-lg font-semibold tracking-tight">AI conflict resolution</span>
                            <span className="text-[10px] uppercase tracking-widest font-semibold px-2 py-0.5 rounded-full" style={{ background: 'var(--ns-clay)', color: 'white' }}>Premium</span>
                        </div>
                        <p className="text-sm text-[var(--ns-text2)] mt-1 leading-relaxed">
                            You can already see the conflict <em>alerts</em> in Group Profile. Premium unlocks the AI&apos;s specific resolution suggestion for each one — woven into the itinerary.
                        </p>
                        <Button
                            onClick={onUpgrade}
                            className="mt-3 rounded-full h-9 px-4 text-white text-xs"
                            style={{ background: 'var(--ns-navy)' }}
                        >
                            <Sparkles size={12} className="mr-1.5" /> Unlock resolutions
                        </Button>
                    </div>
                </div>
            )}

            {/* Day toggle navigation */}
            {itinerary.days?.length > 1 && (
                <div
                    data-testid={ITINERARY.dayTabsContainer}
                    className="flex items-center gap-2 overflow-x-auto -mx-1 px-1 pb-1"
                    style={{ scrollbarWidth: 'thin' }}
                >
                    <button
                        type="button"
                        data-testid={ITINERARY.dayTabAll}
                        onClick={() => setActiveDay('all')}
                        className={`flex-shrink-0 rounded-full h-9 px-4 text-sm font-medium border transition ${
                            activeDay === 'all'
                                ? 'text-white border-transparent'
                                : 'bg-white text-[var(--ns-text2)] border-[var(--ns-border)] hover:text-[var(--ns-text)]'
                        }`}
                        style={activeDay === 'all' ? { background: 'var(--ns-navy)' } : undefined}
                    >
                        All days
                    </button>
                    {itinerary.days.map((d) => {
                        const isActive = activeDay === d.day_number;
                        return (
                            <button
                                key={d.day_number}
                                type="button"
                                data-testid={`${ITINERARY.dayTab}-${d.day_number}`}
                                onClick={() => setActiveDay(d.day_number)}
                                className={`flex-shrink-0 rounded-full h-9 px-4 text-sm font-medium border transition whitespace-nowrap ${
                                    isActive
                                        ? 'text-white border-transparent'
                                        : 'bg-white text-[var(--ns-text2)] border-[var(--ns-border)] hover:text-[var(--ns-text)]'
                                }`}
                                style={isActive ? { background: 'var(--ns-navy)' } : undefined}
                            >
                                <span className="font-semibold">Day {d.day_number}</span>
                                <span className={`ml-2 text-xs ${isActive ? 'text-white/80' : 'text-[var(--ns-text3)]'}`}>
                                    {(() => { try { return format(parseISO(d.date), 'd MMM'); } catch { return d.date; } })()}
                                </span>
                            </button>
                        );
                    })}
                </div>
            )}

            {/* Day-by-day timeline */}
            <div className="space-y-10">
                {itinerary.days?.filter(d => activeDay === 'all' || d.day_number === activeDay).map((d) => (
                    <section key={d.day_number} data-testid={ITINERARY.dayBlock}>
                        <div className="flex items-center gap-3 mb-5">
                            <span className="w-11 h-11 rounded-2xl flex items-center justify-center font-display font-semibold" style={{ background: 'var(--ns-navy)', color: 'white' }}>
                                {d.day_number}
                            </span>
                            <div>
                                <div className="text-xs uppercase tracking-[0.18em] text-[var(--ns-text3)]">Day {d.day_number}</div>
                                <div className="font-display text-2xl font-semibold tracking-tight">{formatDayDate(d.date)}</div>
                            </div>
                            <span className="ml-auto inline-flex items-center gap-1.5 text-xs text-[var(--ns-text3)]">
                                <Sun size={12} /> {d.activities?.length || 0} {d.activities?.length === 1 ? 'activity' : 'activities'}
                            </span>
                        </div>
                        <DayActivities
                            day={d}
                            availableDays={itinerary.days}
                            tripDestination={trip.destination}
                            onCommitReorder={commitReorder}
                            onUpdateField={updateActivityField}
                            onMoveToDay={moveActivityToDay}
                            onEditActivity={openEditEditor}
                            onSaveNote={saveActivityNote}
                            onAddActivity={openAddEditor}
                        />
                    </section>
                ))}
            </div>

            <ActivityEditorSheet
                open={editorOpen}
                onOpenChange={setEditorOpen}
                mode={editorMode}
                dayNumber={editorDay}
                activity={editorActivity}
                activityIndex={editorActivityIndex}
                onSubmit={submitActivityEditor}
                onDelete={editorMode === 'edit' ? deleteEditingActivity : undefined}
            />
        </div>
    );
}
